<template>

  <report-panel icon="$peak" :title="$t('titles.peak_occupancy_times')" :loading="loading">

    <p>
      {{ $t('misc.occupancy_heatmap_summary', {
        weekday_plural: peak.weekday ? $t(`enums.weekdays_plural.${peak.weekday}`) : '?',
        window: windowCategories.find((cat) => cat.name === peak.window)?.label ?? '?',
        rentals: peak.average ? $n(peak.average, {minimumFractionDigits: 0, maximumFractionDigits: 2}) : '?',
      }) }}
    </p>

    <div ref="chartContainer">
      <matrix
        :chart-options="chartOptions"
        :chart-data="chartData"
        :styles="chartStyles"
      />
    </div>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, ref, useTemplateRef } from 'vue'
import { computedAsync } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import ReportPanel from './ReportPanel.vue'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'

// ChartJS
import Matrix from './Matrix'
import { ReportingApi, type GetOnLotByWeekdayResponse } from '../../../../services/api-clients/ReportingApi'
import {
  Chart as ChartJS,
  Tooltip,
  CategoryScale,
  type ChartOptions,
  type ChartData,
  type ChartDataset
} from 'chart.js'
import { MatrixElement, type MatrixDataPoint } from 'chartjs-chart-matrix'
import NotificationService from '../../../../services/NotificationService'
import { Temporal } from '@js-temporal/polyfill'

ChartJS.register(Tooltip, CategoryScale, MatrixElement)

interface Props {
  locationIds: number[]
  channels: string[]
  start: null | Temporal.PlainDate
  end: null | Temporal.PlainDate
}

const props = withDefaults(defineProps<Props>(), {
  channels: () => [],
})

const { n, t } = useI18n()

/**
 * Chart
 */

interface Category {
  name: string
  label: string
}

const weekdayCategories: Category[] = [
  {name: 'mon', label: t('enums.weekdays_abbr.mon')},
  {name: 'tue', label: t('enums.weekdays_abbr.tue')},
  {name: 'wed', label: t('enums.weekdays_abbr.wed')},
  {name: 'thu', label: t('enums.weekdays_abbr.thu')},
  {name: 'fri', label: t('enums.weekdays_abbr.fri')},
  {name: 'sat', label: t('enums.weekdays_abbr.sat')},
  {name: 'sun', label: t('enums.weekdays_abbr.sun')},
]

const windowCategories: Category[] = [
  // TODO: i18n
  {name: '00_02', label: '12 – 2 am'},
  {name: '02_04', label: '2 – 4 am'},
  {name: '04_06', label: '4 – 6 am'},
  {name: '06_08', label: '6 – 8 am'},
  {name: '08_10', label: '8 – 10 am'},
  {name: '10_12', label: '10 am – 12 pm'},
  {name: '12_14', label: '12 – 2 pm'},
  {name: '14_16', label: '2 – 4 pm'},
  {name: '16_18', label: '4 – 6 pm'},
  {name: '18_20', label: '6 – 8 pm'},
  {name: '20_22', label: '8 – 10 pm'},
  {name: '22_00', label: '10 pm – 12 am'},
]

const windowedDataTemplate: {[key: string]: number} = windowCategories.reduce((templateItem, category) => {
  templateItem[category.name] = 0
  return templateItem
}, {} as {[key: string]: number})

const chartColumns = weekdayCategories.map(cat => cat.label)
const chartRows = windowCategories.map(cat => cat.label)


const chartOptions: ChartOptions<'matrix'> = {
  // Based on: https://codepen.io/kurkle/pen/qBbeOZN
  aspectRatio: 1,
  scales: {
    x: {
      type: 'category',
      grid: { display: false },
      labels: chartColumns,
    },
    y: {
      type: 'category',
      offset: true,
      reverse: false, // apparently matrix charts have the Y axis reversed by default, so we need to override that
      grid: { display: false },
      labels: chartRows,
    },
  },
  plugins: {
    // TODO: add solid border lines at top and right? https://www.chartjs.org/docs/3.7.1/samples/plugins/chart-area-border.html
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
      callbacks: {
        title: (context) => {
          return `${context[0].label} ${context[0].formattedValue}`
        },
        label: (context) => {
          // @ts-ignore: Don't complain that MatrixDataPoint doesn't have a v property (docs say that it does)
          return n((context.raw as MatrixDataPoint).v, {style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 2})
        },
      },
    },
  },
}

/**
 * Data retrieval
 */

const loading = ref<boolean>(false)
const pendingRequestAbortControllers: AbortController[] = []

const rawData = computedAsync<null | GetOnLotByWeekdayResponse>(

  async () => {

    if (
      props.locationIds.length === 0 ||
      !props.start ||
      !props.end
    ) {
      return null
    }

    pendingRequestAbortControllers.forEach((controller) => {
      controller.abort()
    })
    pendingRequestAbortControllers.length = 0

    const abortController = new AbortController()
    pendingRequestAbortControllers.push(abortController)

    loading.value = true
    let data: null | GetOnLotByWeekdayResponse = null

    await ReportingApi.getOnLotByWeekday({
      locationIds: props.locationIds,
      start: props.start,
      end: props.end,
      signal: abortController.signal,
    })
    .then((response) => {
      data = response
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error('Couldn\'t load peak rental time data!')
    })
    .finally(() => {
      loading.value = false
    })

    return data    
    
  },

  null // default value

)

/**
 * Totals the on-lot stats by weekday, filtered by channel.
 */

interface WeekdayTotal {
  days: number
  onLot: {[key: string]: number}
}

type WeekdayTotals = {[key: string]: WeekdayTotal}

const totalsByWeekday = computed<WeekdayTotals>(() => {

  const totals: WeekdayTotals = {
    mon: {days: 0, onLot: {...windowedDataTemplate}},
    tue: {days: 0, onLot: {...windowedDataTemplate}},
    wed: {days: 0, onLot: {...windowedDataTemplate}},
    thu: {days: 0, onLot: {...windowedDataTemplate}},
    fri: {days: 0, onLot: {...windowedDataTemplate}},
    sat: {days: 0, onLot: {...windowedDataTemplate}},
    sun: {days: 0, onLot: {...windowedDataTemplate}},
  }

  if (rawData.value) {

    Object.entries(rawData.value).forEach(([weekday, weekdayItem]) => {
      Object.entries(weekdayItem.by_window).forEach(([wndow, windowItem]) => {

        const windowTotal = Object.entries(windowItem.by_channel)
          .filter(([channel, _value]) => props.channels.length === 0 || props.channels.includes(channel))
          .reduce((total, [_channel, value]) => total += value, 0)

        totals[weekday].days = weekdayItem.num_dates
        totals[weekday].onLot[wndow] = windowTotal

      })
    })

  }

  return totals

})

/**
 * 
 */

type WeekdayAverages = {
  [key: string]: {[key: string]: number}
}

const averagesByWeekday = computed<WeekdayAverages>(() => {

  const averages: WeekdayAverages = {
    mon: {...windowedDataTemplate},
    tue: {...windowedDataTemplate},
    wed: {...windowedDataTemplate},
    thu: {...windowedDataTemplate},
    fri: {...windowedDataTemplate},
    sat: {...windowedDataTemplate},
    sun: {...windowedDataTemplate},
  }

  Object.entries(totalsByWeekday.value).forEach(([weekday, totalItem]) => {
    Object.entries(totalItem.onLot).forEach(([wndow, onLot]) => {
      averages[weekday][wndow] = totalItem.days ? onLot / totalItem.days : 0
    })

  })

  return averages

})

/**
 * Finds the weekday/window cell with the highest value.
 */

interface Peak {
  weekday: null | string
  window: null | string
  average: null | number
}

const peak = computed<Peak>(() => {

  const output: Peak = {
    weekday: null,
    window: null,
    average: null,
  }

  Object.entries(averagesByWeekday.value).forEach(([weekday, averageItem]) => {
    Object.entries(averageItem).forEach(([wndow, average]) => {
      if (output.average === null || average > output.average) {
        output.weekday = weekday
        output.window = wndow
        output.average = average
      }
    })
  })

  return output

})

/**
 * The data that actually gets fed into the chart
 */
const chartData = computed<ChartData<'matrix', MatrixDataPoint[]>>(() => {

  const data: MatrixDataPoint[] = []

  Object.entries(averagesByWeekday.value).forEach(([weekday, averageItem]) => {
    const xLabel = weekdayCategories.find(cat => cat.name === weekday)?.label ?? weekday
    Object.entries(averageItem).forEach(([wndow, average]) => {
      const yLabel = windowCategories.find(cat => cat.name === wndow)?.label ?? wndow
      // @ts-ignore: Don't complain that MatrixDataPoint.x and .y are typed as numbers (docs say strings are OK: https://chartjs-chart-matrix.pages.dev/samples/category.html)
      data.push({x: xLabel, y: yLabel, v: average})
    })
  })

  const dataset: ChartDataset<'matrix'> = {
    data: data,
    borderWidth: 1,
    backgroundColor: (context) => {
      if (context.dataIndex === undefined) {
        return
      }
      // @ts-ignore: Don't complain that MatrixDataPoint doesn't have a v property (docs say that it does)
      const value = context.dataset.data[context.dataIndex].v
      const percentage = peak.value?.average ? Math.round(100 - (value / peak.value.average * 100)) : 0
      return `hwb(200 ${percentage}% 0%)`
    },
    width: (context) => {
      const a = context.chart.chartArea
      if (!a) {
        return 0
      }
      return (a.right - a.left) / chartColumns.length
    },
    height: (context) => {
      const a = context.chart.chartArea
      if (!a) {
        return 0
      }
      return (a.bottom - a.top) / chartRows.length
    }
  }

  return {
    datasets: [dataset],
  }

})

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const container = useTemplateRef('chartContainer')
const { chartStyles } = useFitChartToContainer(container)

</script>
